'use client'

import React, { ReactNode, useContext } from 'react'
import Link from 'next/link'
import { MainNavigationListItemContext } from './MainNavigationListItemContext'

export interface MainNavigationItemProps {
    isActive?: boolean
    title: ReactNode
    hasSubNavigation?: boolean
    href?: string
    ariaCurrent?: 'page'
    dataTestId?: string
}

const baseClasses = 'main-navigation__item px-3 py-0 h-full inline-flex text-base bg-transparent'

const innerBaseClasses = 'inline-flex items-center text-center justify-center h-full border-y-2 border-t-transparent'

// The bottom border always takes up 2px (see border-y-2 above) so items keep the same height in
// both states. The inactive branch must therefore paint it transparent.
const activeIndicatorClasses = (isActive?: boolean) => (isActive ? 'border-b-accent-700' : 'border-b-transparent')

export const MainNavigationItem = ({
    isActive,
    title,
    hasSubNavigation = false,
    href,
    ariaCurrent,
    dataTestId,
}: MainNavigationItemProps) => {
    const context = useContext(MainNavigationListItemContext)

    const selectedClasses = isActive
        ? 'text-text-interactive'
        : 'text-text-secondary hover:bg-accent-opacity-100 hover:text-text-interactive active:bg-accent-opacity-100 active:text-text-interactive'

    if (hasSubNavigation && context) {
        const { toggle, overlay } = context

        return (
            <button
                type="button"
                ref={overlay.triggerRef as React.Ref<HTMLButtonElement>}
                className={`${baseClasses} ${selectedClasses} cursor-default outline-hidden`}
                {...overlay.triggerAriaProps}
                data-testid={dataTestId ?? 'main-menu-item'}
                onClick={toggle}
            >
                <span className={`${innerBaseClasses} ${activeIndicatorClasses(isActive)} gap-1`} aria-hidden={false}>
                    {title}
                </span>
            </button>
        )
    }

    if (href) {
        return (
            <Link
                href={href}
                aria-current={ariaCurrent}
                className={`${baseClasses} ${selectedClasses}`}
                data-testid={dataTestId ?? 'main-menu-item'}
            >
                <span className={`${innerBaseClasses} ${activeIndicatorClasses(isActive)}`}>{title}</span>
            </Link>
        )
    }

    return (
        <div className={`${baseClasses} ${selectedClasses}`} data-testid={dataTestId ?? 'main-menu-item'}>
            <span className={`${innerBaseClasses} ${activeIndicatorClasses(isActive)}`}>{title}</span>
        </div>
    )
}
